import { useCallback, useRef, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Input, Tag, Modal, message } from 'antd';
import { PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import { listContacts, deleteContact, type Contact, type ContactFilters } from '@/api/contactApi';
import { useAuth } from '@/hooks/useAuth';

export default function ContactListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [filters, setFilters] = useState<ContactFilters>({});

  const canWrite = hasPermission('contacts', 'write');
  const canDelete = hasPermission('contacts', 'delete');

  const handleFilterChange = useCallback(
    (key: keyof ContactFilters, value: string | undefined) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const fetchData = useCallback(
    (params: SearchParams) => listContacts(params, filters),
    [filters]
  );

  const filterMounted = useRef(false);
  useEffect(() => {
    if (!filterMounted.current) {
      filterMounted.current = true;
      return;
    }
    gridRef.current?.reload();
  }, [filters]);

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('contacts.confirm_delete'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteContact(id);
            message.success(t('contacts.delete_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const columns: ColumnsType<Contact> = useMemo(
    () => [
      {
        title: t('contacts.field.name'),
        dataIndex: 'name',
        key: 'name',
        sorter: true,
        render: (name: string, record: Contact) => (
          <a onClick={() => navigate(`/contacts/${record.id}`)}>{name}</a>
        ),
      },
      {
        title: t('contacts.field.email'),
        dataIndex: 'email',
        key: 'email',
        ellipsis: true,
      },
      {
        title: t('contacts.field.phone'),
        dataIndex: 'phone',
        key: 'phone',
      },
      {
        title: t('contacts.field.title'),
        dataIndex: 'title',
        key: 'title',
        ellipsis: true,
      },
      {
        title: t('contacts.field.department'),
        dataIndex: 'department',
        key: 'department',
        ellipsis: true,
      },
      {
        title: t('contacts.field.is_primary'),
        dataIndex: 'is_primary',
        key: 'is_primary',
        width: 100,
        render: (val: boolean) =>
          val ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : null,
      },
      {
        title: t('contacts.field.tags'),
        dataIndex: 'tags',
        key: 'tags',
        ellipsis: true,
        render: (tags: string[]) =>
          (tags?.length ?? 0) > 0
            ? tags.map((tag) => <Tag key={tag}>{tag}</Tag>)
            : '-',
      },
      {
        title: t('contacts.field.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        width: 170,
        render: (val: string) => (val ? new Date(val).toLocaleString() : '-'),
      },
      {
        title: t('app.actions'),
        key: 'actions',
        width: 150,
        render: (_: unknown, record: Contact) => (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/contacts/${record.id}`)}
            />
            {canWrite && (
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => navigate(`/contacts/${record.id}/edit`)}
              />
            )}
            {canDelete && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
              />
            )}
          </Space>
        ),
      },
    ],
    [t, navigate, canWrite, canDelete, handleDelete]
  );

  const filterBar = (
    <Space wrap>
      <Input.Search
        placeholder={t('contacts.filter.department')}
        style={{ width: 180 }}
        allowClear
        onSearch={(val) => handleFilterChange('department', val || undefined)}
        enterButton={false}
      />
    </Space>
  );

  const toolbar = (
    <Space>
      {filterBar}
      {canWrite && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/contacts/new')}
        >
          {t('contacts.new_contact')}
        </Button>
      )}
    </Space>
  );

  return (
    <div>
      <h2>{t('contacts.list_title')}</h2>
      <DataGrid<Contact>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('contacts.search_placeholder')}
        defaultSortField="created_at"
        defaultSortDescending
        toolbar={toolbar}
        onRow={(record) => ({
          onDoubleClick: () => navigate(`/contacts/${record.id}`),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
}
